/**
 * Aggregate Command Service
 * 
 * Application service for aggregate write operations following CQRS pattern.
 * Encapsulates business use cases and orchestrates domain operations.
 */

import { injectable, inject } from 'inversify';
import { Aggregate, AggregateProperties } from '../../domain/entities/Aggregate';
import type { IAggregateRepository } from '../../../meta-model-design/domain/ports/IAggregateRepository';
import type { IBoundedContextRepository } from '../../../meta-model-design/domain/ports/IBoundedContextRepository';
import { CreateAggregatePayload } from '../commands/CreateAggregateCommand';
import { DDD_SYMBOLS } from '../../../../infrastructure/di/DDD_SYMBOLS';

export interface CreateAggregateResult {
  aggregateId: string;
  success: boolean;
  message: string;
}

@injectable()
export class AggregateCommandService {
  constructor(
    @inject(DDD_SYMBOLS.IAggregateRepository) private aggregateRepository: IAggregateRepository,
    @inject(DDD_SYMBOLS.IBoundedContextRepository) private boundedContextRepository: IBoundedContextRepository
  ) {}

  async createAggregate(payload: CreateAggregatePayload): Promise<CreateAggregateResult> {
    // Validate bounded context exists
    let boundedContext = await this.boundedContextRepository.findById(payload.boundedContextId);
    if (!boundedContext) {
      throw new Error(`Bounded context not found: ${payload.boundedContextId}`);
    }

    // Check for duplicate aggregate names within the bounded context
    const existingAggregates = await this.aggregateRepository.findByBoundedContext(payload.boundedContextId);
    const duplicateName = existingAggregates.find(agg => 
      agg.name.toLowerCase() === payload.name.toLowerCase()
    );
    if (duplicateName) {
      throw new Error(`Aggregate with name "${payload.name}" already exists in this bounded context`);
    }

    // Build aggregate properties
    const properties: AggregateProperties = {
      boundedContextId: payload.boundedContextId,
      name: payload.name,
      description: payload.description,
      responsibility: payload.responsibility,
      rootEntity: {
        id: crypto.randomUUID(),
        name: payload.rootEntity.name,
        description: payload.rootEntity.description,
        properties: payload.rootEntity.properties.map(prop => ({
          id: crypto.randomUUID(),
          name: prop.name,
          type: this.parsePropertyType(prop.type),
          description: prop.description,
          isRequired: prop.isRequired,
          isCollection: false,
          constraints: []
        })),
        methods: []
      },
      childEntities: [],
      valueObjects: payload.initialValueObjects?.map(vo => ({
        id: crypto.randomUUID(),
        name: vo.name,
        description: vo.description,
        properties: vo.properties.map(prop => ({
          id: crypto.randomUUID(),
          name: prop.name,
          type: this.parsePropertyType(prop.type),
          description: prop.description,
          isRequired: prop.isRequired,
          constraints: []
        })),
        validationRules: []
      })) || [],
      businessInvariantIds: [],
      domainEventsIds: [],
      ubiquitousLanguageTerms: payload.ubiquitousLanguageTerms || []
    };

    // Generate AR-XXX aggregate ID
    let aggregateId = payload.aggregateId;
    if (!aggregateId) {
      aggregateId = await this.aggregateRepository.getNextDomainId('AR');
    }
    
    // Validate AR-XXX pattern if provided
    if (payload.aggregateId && !payload.aggregateId.match(/^AR-\d{3}$/)) {
      throw new Error('Aggregate ID must follow AR-XXX pattern');
    }

    // Create aggregate with proper AR-XXX ID using reconstitute to bypass validation during creation
    const aggregateWithId = Aggregate.reconstitute(
      properties,
      aggregateId,
      new Date(),
      new Date()
    );
    
    await this.aggregateRepository.save(aggregateWithId);

    return {
      aggregateId: aggregateWithId.id,
      success: true,
      message: `Aggregate "${payload.name}" created successfully with ID ${aggregateWithId.id}`
    };
  }

  async updateAggregate(aggregateId: string, updates: Partial<AggregateProperties>): Promise<void> {
    const aggregate = await this.aggregateRepository.findById(aggregateId);
    if (!aggregate) {
      throw new Error(`Aggregate not found: ${aggregateId}`);
    }

    // Apply updates to aggregate
    if (updates.name) {
      aggregate.updateName(updates.name);
    }
    if (updates.description !== undefined) {
      aggregate.updateDescription(updates.description);
    }
    if (updates.responsibility) {
      aggregate.updateResponsibility(updates.responsibility);
    }

    await this.aggregateRepository.save(aggregate);
  }

  async deleteAggregate(aggregateId: string): Promise<void> {
    const exists = await this.aggregateRepository.exists(aggregateId);
    if (!exists) {
      throw new Error(`Aggregate not found: ${aggregateId}`);
    }

    await this.aggregateRepository.delete(aggregateId);
  }

  private parsePropertyType(typeString: string): any {
    // Simple type parsing - in a real implementation, this would be more sophisticated
    const primitiveTypes = ['string', 'number', 'boolean', 'date', 'uuid', 'email', 'url'];
    
    if (primitiveTypes.includes(typeString.toLowerCase())) {
      return typeString.toLowerCase();
    }

    // Check for enum pattern: enum:value1,value2,value3
    if (typeString.startsWith('enum:')) {
      const enumValues = typeString.substring(5).split(',').map(v => v.trim());
      return { enum: enumValues };
    }

    // Check for value object pattern: vo:ValueObjectName
    if (typeString.startsWith('vo:')) {
      const voName = typeString.substring(3).trim();
      return { valueObject: voName };
    }

    // Check for entity pattern: entity:EntityName
    if (typeString.startsWith('entity:')) {
      const entityName = typeString.substring(7).trim();
      return { entity: entityName };
    }

    // Default to string for unknown types
    return 'string';
  }
}